import { all, any, bool, is, isIn, object, string, pointTest, isMacOS } from '@moleculeone/utils';
import { namedMols } from '@moleculeone/draw';

import {
  CLEAR_CHIRAL_BOND,
  CLEAR_GEOMETRY_BOND,
  FLIP_BOND_DIRECTION,
  FLIP_BOND_GEOMETRY,
  FLIP_MOL,
  KEY_DOWN_EVENT,
  MOL_MOUSE_EVENT,
  MOUSE_DOWN,
  MOUSE_MOVE,
  MOUSE_UP,
  PARSE_SMILES,
  PICK_ELEMENT,
  REDRAW,
  ROTATE_AROUND_BOND,
  SET_CHIRAL_BOND,
  SET_GEOMETRY_BOND,
  SET_MODE,
  SET_FRAGMENT_ATOM_MODE,
  SET_MOL,
  KEY_UP_EVENT,
  SET_FRAGMENT_BOND_MODE,
  PARSE_NAME,
  SET_MOLS,
} from '../../etc/constants';

export const CLIPBOARD_PASTE_FULFILL = 'CLIPBOARD_PASTE_FULFILL';

export function onKeyEvent(event: any, dir: string) {
  let type;
  if (dir === 'DOWN') {
    type = KEY_DOWN_EVENT;
  } else if (dir === 'UP') {
    type = KEY_UP_EVENT;
  } else {
    throw Error('invalid key dir');
  }

  return {
    type,
    key: event.key,
    ctrlKey: isMacOS() ? event.metaKey : event.ctrlKey,
    altKey: event.altKey,
    shiftKey: event.shiftKey,
    dir,
  };
}

export function molMouseEvent(event: any, molIndex: string) {
  if (isMacOS()) {
    event.ctrlKey = event.metaKey;
  }
  delete event.metaKey;
  return {
    type: MOL_MOUSE_EVENT,
    event,
    molIndex,
  };
}

export const REMOVE_ATOM = 'REMOVE_ATOM';
export const REMOVE_BOND = 'REMOVE_BOND';
export const ADD_ATOM = 'ADD_ATOM';
export const ADD_BOND = 'ADD_BOND';
export const EDIT_ATOM = 'EDIT_ATOM';
export const EDIT_BOND = 'EDIT_BOND';
export const REPOSITION = 'REPOSITION';

export const WEDGE = 'wedge';
export const DASHED = 'dashed';
export const CIS = 'CIS';
export const TRANS = 'TRANS';

export function setMol({
  mol,
  molIndex,
  useActiveMol = false,
}: {
  mol: any;
  molIndex: string;
  useActiveMol?: boolean;
}) {
  return {
    type: SET_MOL,
    data: {
      mol,
      molIndex,
      useActiveMol,
    },
  };
}

export function setMols({ mols, initial = false }: { mols: any[]; initial?: boolean }) {
  return {
    type: SET_MOLS,
    data: {
      mols,
      initial,
    },
  };
}

export function setFragmentAtomMode(fragment: any, idx: string) {
  return {
    type: SET_FRAGMENT_ATOM_MODE,
    fragment,
    idx,
  };
}

export function setFragmentBondMode(fragment: any, idx: string) {
  return {
    type: SET_FRAGMENT_BOND_MODE,
    fragment,
    idx,
  };
}

export function setPlainMode(mode: any) {
  return {
    type: SET_MODE,
    mode,
  };
}

export function parseSmiles(smiles: string) {
  return {
    type: PARSE_SMILES,
    smiles,
  };
}

export function parseName(name: string) {
  return {
    type: PARSE_NAME,
    name,
  };
}

export function repositionRequest(smiles: string) {
  return {
    type: REDRAW,
    smiles,
  };
}

export function pickElement(event: any, element: any) {
  return {
    type: PICK_ELEMENT,
    event,
    element,
  };
}

export function flipBond(bondIdx: number) {
  return {
    type: FLIP_BOND_DIRECTION,
    idx: bondIdx,
  };
}

export function clearChiral(bondIdx: number) {
  return {
    type: CLEAR_CHIRAL_BOND,
    idx: bondIdx,
  };
}

export function setChiral(bondIdx: number, chiralType: string) {
  return {
    type: SET_CHIRAL_BOND,
    idx: bondIdx,
    chiralType,
  };
}

export function setGeometry(bondIdx: number) {
  return {
    type: SET_GEOMETRY_BOND,
    idx: bondIdx,
  };
}

export function clearGeometry(bondIdx: number) {
  return {
    type: CLEAR_GEOMETRY_BOND,
    idx: bondIdx,
  };
}

export function flipGeometry(bondIdx: number) {
  return {
    type: FLIP_BOND_GEOMETRY,
    idx: bondIdx,
  };
}
export function rotateAroundBond(bondIdx: number) {
  return {
    type: ROTATE_AROUND_BOND,
    idx: bondIdx,
  };
}
export function flipMol(axis: any) {
  return {
    type: FLIP_MOL,
    axis,
  };
}

export const molEventCheck = all(
  object({
    clientPos: pointTest,
    svgPos: pointTest,
    pagePos: pointTest,
    type: isIn(MOUSE_MOVE, MOUSE_DOWN, MOUSE_UP),
    ctrlKey: bool,
    altKey: bool,
    shiftKey: bool,
  }),
  any(
    object({
      empty: is(true),
    }),
    object({
      atom: is(true),
      idx: string,
    }),
    object({
      bond: is(true),
      start: string,
      end: string,
    })
  )
);

export function setMode(mode: any) {
  return {
    type: SET_MODE,
    mode,
  };
}

export function redrawMol(molIndex?: string) {
  return {
    type: REDRAW,
    molIndex,
  };
}

export function clearMol(molIndex?: string) {
  return {
    type: SET_MOL,
    data: {
      mol: namedMols.emptyMol(),
      molIndex,
    },
  };
}
